@if (showWorkspaceChrome()) {
  <header
    class="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
    <div
      class="mx-auto flex max-w-page items-center justify-between gap-4 px-4 py-3.5 sm:px-6 lg:px-8">
      <div class="flex min-w-0 items-center gap-5 sm:gap-8">
        <a
          routerLink="/"
          class="flex min-w-0 cursor-pointer items-center gap-3">
          <img
            src="assets/brand/logo.svg"
            width="36"
            height="36"
            alt="FormOps logo"
            class="h-9 w-9 shrink-0" />
          <p class="text-sm font-semibold text-slate-950">FormOps</p>
        </a>
        <a
          routerLink="/my-forms/inbox"
          class="cursor-pointer rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
          My list
        </a>
      </div>

      <app-respondent-account-menu-button />
    </div>
  </header>
} @else if (showPasswordChrome()) {
  <header
    class="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
    <div
      class="mx-auto flex max-w-page items-center justify-between gap-4 px-4 py-3.5 sm:px-6 lg:px-8">
      <div class="flex min-w-0 items-center gap-3">
        <img
          src="assets/brand/logo.svg"
          width="36"
          height="36"
          alt="FormOps logo"
          class="h-9 w-9 shrink-0" />
        <div class="min-w-0">
          <p class="truncate text-sm font-semibold text-slate-950">
            {{ title() }}
          </p>
          <p class="truncate text-xs text-slate-500">Password protected</p>
        </div>
      </div>

      <lib-signal-button
        appearance="outline"
        [fullWidth]="false"
        (clicked)="leavePasswordSession()">
        Leave
      </lib-signal-button>
    </div>
  </header>
} @else if (showPublicChrome()) {
  <header
    class="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
    <div
      class="mx-auto flex max-w-page items-center justify-between gap-4 px-4 py-3.5 sm:px-6 lg:px-8">
      <a
        routerLink="/"
        class="flex min-w-0 cursor-pointer items-center gap-3">
        <img
          src="assets/brand/logo.svg"
          width="36"
          height="36"
          alt="FormOps logo"
          class="h-9 w-9 shrink-0" />
        <p class="text-sm font-semibold text-slate-950">FormOps</p>
      </a>

      <lib-signal-button
        appearance="outline"
        [fullWidth]="false"
        (clicked)="closePublicForm()">
        Close
      </lib-signal-button>
    </div>
  </header>
}

@switch (view()) {
  @case ('loading') {
    <div class="flex min-h-dvh items-center justify-center px-4">
      <p class="text-sm text-slate-500">Loading form…</p>
    </div>
  }
  @case ('redirecting') {
    <div class="flex min-h-dvh items-center justify-center px-4">
      <p class="text-sm text-slate-500">Redirecting to sign in…</p>
    </div>
  }
  @case ('form') {
    <div
      [class]="
        'flex min-h-dvh w-full flex-1 items-start justify-center overflow-y-auto px-4 pb-10 sm:px-8 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="flex w-full max-w-3xl flex-col gap-3">
        @if (submitError(); as err) {
          <div
            class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {{ err }}
          </div>
        }
        @if (form(); as builtForm) {
          <app-form-preview-card
            [form]="builtForm"
            mode="desktop"
            [enableSubmit]="true"
            [submitting]="submitting()"
            [initialAnswers]="draftAnswers()"
            [publicContext]="publicContext()"
            (answersChanged)="onAnswersChanged($event)"
            (submitAnswers)="onSubmitAnswers($event)" />
        }
      </div>
    </div>
  }
  @case ('success') {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-md items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
        <h1 class="text-xl font-semibold text-slate-950">
          {{ requireApproval() ? 'Response received' : 'Thank you' }}
        </h1>
        <p class="mt-3 text-sm leading-6 text-slate-500">
          @if (requireApproval()) {
            Your response was submitted and is pending review.
          } @else {
            Your response was submitted successfully.
          }
        </p>
        @if (submissionReference(); as reference) {
          <div
            class="mt-6 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
            <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Reference
            </p>
            <p class="mt-1 text-base font-semibold tracking-wide text-slate-950">
              {{ reference }}
            </p>
          </div>
        }
      </div>
    </section>
  }
  @case ('limit_reached') {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-md items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
        <h1 class="text-xl font-semibold text-slate-950">Limit reached</h1>
        <p class="mt-3 text-sm leading-6 text-slate-500">
          This form is no longer accepting responses.
        </p>
      </div>
    </section>
  }
  @case ('password') {
    <section class="mx-auto flex min-h-dvh max-w-md items-center justify-center px-4 py-10">
      <div class="w-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
        <div class="flex flex-col gap-2 text-center">
          <h1 class="text-xl font-semibold text-slate-950">{{ title() }}</h1>
          <p class="text-sm text-slate-500">
            This form is password protected. Enter the password to continue.
          </p>
        </div>

        <form class="mt-8 flex flex-col gap-5" [signalForm]="passwordForm">
          <lib-signal-text-field
            [formField]="passwordForm.password"
            label="Password"
            inputType="password"
            placeholder="Enter password"
            autocomplete="current-password" />

          <lib-signal-button
            [fullWidth]="true"
            [loading]="unlockState.loading()"
            (clicked)="onUnlock()">
            Continue
          </lib-signal-button>
        </form>
      </div>
    </section>
  }
  @case ('not_yet_available') {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-lg items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl bg-white p-8 text-center sm:p-10">
        <div class="mx-auto mb-6 w-full max-w-[280px]">
          <img
            src="assets/illustrations/form-scheduled.svg"
            alt=""
            class="h-auto w-full object-contain"
            width="520"
            height="340" />
        </div>
        <h1 class="text-2xl font-semibold tracking-tight text-slate-950 sm:text-[1.75rem]">
          {{ title() }}
        </h1>
        <p class="mt-2 text-sm leading-6 text-slate-500">
          This form hasn’t opened yet. It will be available at the scheduled time.
        </p>
        @if (availableAt(); as opensAt) {
          <div
            class="mt-8 flex w-full items-center gap-2.5 rounded-2xl bg-indigo-50/90 px-5 py-4 text-left sm:px-6 sm:py-5">
            <span
              class="flex h-11 w-11 shrink-0 items-center justify-center text-indigo-500"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/calendar-blank-outline.svg" [size]="28" />
            </span>
            <div class="min-w-0">
              <p class="text-sm text-slate-500">Available from</p>
              <p class="mt-0.5 text-base font-semibold text-slate-950">
                {{ opensAt | date: 'MMM d, y, h:mm a' }}
              </p>
            </div>
          </div>
        }
      </div>
    </section>
  }
  @case ('closed') {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-lg items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl bg-white p-8 text-center sm:p-10">
        <div class="mx-auto mb-6 w-full max-w-[280px]">
          <img
            src="assets/illustrations/form-closed.svg"
            alt=""
            class="h-auto w-full object-contain"
            width="520"
            height="340" />
        </div>
        <h1 class="text-2xl font-semibold tracking-tight text-slate-950 sm:text-[1.75rem]">
          {{ title() }}
        </h1>
        <p class="mx-auto mt-2 max-w-[17.5rem] text-sm leading-6 text-slate-500">
          This form is closed and is no longer accepting responses.
        </p>
        @if (closedAt(); as endedAt) {
          <div
            class="mt-8 flex w-full items-center gap-2.5 rounded-2xl bg-red-50 px-5 py-4 text-left sm:px-6 sm:py-5">
            <span
              class="flex h-11 w-11 shrink-0 items-center justify-center text-red-500"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/calendar-blank-outline.svg" [size]="28" />
            </span>
            <div class="min-w-0">
              <p class="text-sm text-slate-500">Closed on</p>
              <p class="mt-0.5 text-base font-semibold text-slate-950">
                {{ endedAt | date: 'MMM d, y, h:mm a' }}
              </p>
            </div>
          </div>
        }
      </div>
    </section>
  }
  @case ('forbidden') {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-md items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
        <h1 class="text-xl font-semibold text-slate-950">Access denied</h1>
        <p class="mt-3 text-sm leading-6 text-slate-500">
          You do not have permission to open this form.
        </p>
      </div>
    </section>
  }
  @case ('not_found') {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-md items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
        <h1 class="text-xl font-semibold text-slate-950">Form not found</h1>
        <p class="mt-3 text-sm leading-6 text-slate-500">
          This form is unavailable or the link may be incorrect.
        </p>
      </div>
    </section>
  }
  @default {
    <section
      [class]="
        'mx-auto flex min-h-dvh max-w-md items-center justify-center px-4 pb-10 ' +
        (showChrome() ? 'pt-[calc(4.25rem+2.5rem)]' : 'py-10')
      ">
      <div class="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
        <h1 class="text-xl font-semibold text-slate-950">Something went wrong</h1>
        <p class="mt-3 text-sm leading-6 text-slate-500">
          We could not load this form. Please try again later.
        </p>
      </div>
    </section>
  }
}
